import React, { Fragment, useEffect, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { Add, Check, Delete, Edit, Pending, Summarize } from "@mui/icons-material";
import CompanyDeleteModal from "./CompanyDeleteModal";
import CompanyAddModal from "./CompanyAddModal";
import CompanyEditModal from "./CompanyEditModal";
import companyService from "../../services/companyService";
import ExposureReportModal from "./ExposureReportModal";
import userService from "../../services/userService";
import CompanyAddBreachAwareModal from "./CompanyAddBreachAwareModal";
import CompanyAddUsecureModal from "./CompanyAddUsecureModal";
import { pink } from "../../shared/colours";
import CompanyKyndLiveModal from "./CompanyKyndLiveModal";
import SubmitModal from "./SubmitModal";
import reportService from "../../services/reportService";
import MaterialTable from "material-table";
import { dataCell, dataHeader, defaultTableOptions, tableIcons, textCell, textHeader } from "../../shared/tableHelper";

const AdminCompaniesTable = (props) => {
	
	let [ companies, setCompanies ] = useState(companyService.companies);
	let [ deleteCompany, setDeleteCompany ] = useState(null);
	let [ editCompany, setEditCompany ] = useState(null);
	let [ showAddCompany, setShowAddCompany ] = useState(false);
	let [ showKyndLive, setShowKyndLive ] = useState(null);
	let [ showAddBreachAware, setShowAddBreachAware ] = useState(null);
	let [ showAddUsecure, setShowAddUsecure ] = useState(null);
	let [ exposureReportCompany, setExposureReportCompany ] = useState(false);
	let [ selectedCompany, setSelectedCompany ] = useState(companyService.selectedCompany);

	let [ showSubmitModal, setShowSubmitModal ] = useState(false);
	let [ sentSuccess, setSentSuccess ] = useState(false);
	
	useEffect(() => {
		companyService.onSelectCompany(() => {
			setCompanies(companyService.companies);
		});
	}, []);
	
	// EXPOSURE REPORT
	const handleReportClick = async (company) => {
		companyService.getPrimaryDomain(company)
		.then((domain) => {
			if (domain && domain.address) {
				company.domain = domain.address;
			}
			setExposureReportCompany(company);
		})
		.catch((err) => {
			console.log(err);
		});
	};
	
	const handleExposureReportClose = () => {
		setExposureReportCompany(null);
	};
	
	const handleExposureReportSubmit = (company, exposureReportDetails, useClearbitLogo, advanced) => {
		reportService.exposureReport(company, exposureReportDetails, useClearbitLogo, advanced);
	};

	// EDIT COMPANY
	const handleEditClick = (company) => {
		setEditCompany(company);
	};
	
	const handleEditClose = () => {
		setEditCompany(null);
	};
	
	const handleEditSubmit = (company) => {
		companyService.update(company)
		.then((company) => {
			setCompanies(companyService.companies);
			return companyService.selectCompany(company);
		})
		.then((company) => {
			setSelectedCompany(company);
		})
		.catch((err) => {
			console.log(`Error adding company: ${err.message}`);
		})
		.finally(() => {
			setEditCompany(null);
		});
	};
	
	// DELETE COMPANY
	const handleDeleteClick = (company) => {
		setDeleteCompany(company);
	};
	
	const handleConfirmDelete = (confirm) => {
		if (confirm) {
			companyService.delete(deleteCompany)
			.then(() => {
				setCompanies(companyService.companies);
				const company = (companies && companies.length > 0)? companies[0] : null;
				return companyService.selectCompany(company);
			})
			.then((company) => {
				setSelectedCompany(company);
			})
			.catch((err) => {
				console.log(`Error deleting company: ${err.message}`);
			})
			.finally(() => {
				setDeleteCompany(null);
			});
		}
		else {
			setDeleteCompany(null);
		}
	};
	
	// ADD COMPANY
	const handleAddClick = () => {
		setShowAddCompany(true);
	};
	
	const handleAddClose = () => {
		setShowAddCompany(false);
	};
	
	const handleAddSubmit = (company, user) => {
		companyService.add(company, user)
		.then((company) => {
			setCompanies(companyService.Companies());
			return companyService.selectCompany(company);
		})
		.then((company) => {
			setSelectedCompany(company);
		})
		.catch((err) => {
			console.log(`Error adding company: ${err.message}`);
		})
		.finally(() => {
			setShowAddCompany(false);
		});
	};
	
	// KYND LIVE
	const handleKyndLiveClick = (company) => {
		setShowKyndLive(company);
	};
	
	const handleKyndLiveClose = () => {
		setShowKyndLive(null);
	};
	
	const handleKyndLiveSubmit = (kyndLiveRequest) => {
		companyService.kyndLive(kyndLiveRequest)
		.catch((err) => {
			console.log(`Error adding company breachaware: ${err.message}`);
		})
		.finally(() => {
			setShowKyndLive(null);
			setCompanies(companyService.companies);
		});
	};
	
	// ADD BREACHAWARE
	const handleAddBreachAwareClick = (company) => {
		setShowAddBreachAware(company);
	};
	
	const handleAddBreachAwareClose = () => {
		setShowAddBreachAware(null);
	};
	
	const handleAddBreachAwareSubmit = (breachAwareRequest) => {
		companyService.addBreachAware(breachAwareRequest)
		.catch((err) => {
			console.log(`Error adding company breachaware: ${err.message}`);
			setShowAddBreachAware(null);
			setCompanies(companyService.companies);
		})
		.finally(() => {
			setShowAddBreachAware(null);
			setCompanies(companyService.companies);
		});
	};
	
	// ADD USECURE
	const handleAddUsecureClick = (company) => {
		setShowAddUsecure(company);
	};
	
	const handleAddUsecureClose = () => {
		setShowAddUsecure(null);
	};
	
	const handleAddUsecureSubmit = (usecureRequest) => {
		companyService.addUsecure(usecureRequest)
		.catch((err) => {
			console.log(`Error adding company usecure: ${err.message}`);
		})
		.finally(() => {
			setShowAddUsecure(null);
			setCompanies(companyService.companies);
		});
	};
	
	// SELECT COMPANY
	const handleNameClick = (company) => {
		setSentSuccess(false);
		setShowSubmitModal(true);
		companyService.selectCompany(company)
		.then((company) => {
			handleSubmitModalClose();
			setSelectedCompany(company);
		});
	};
	
	const handleSubmitModalClose = () => {
		setShowSubmitModal(false);
	};
	
	return (
		<React.Fragment>
		{(userService.AccessLevel() === 1)?
			<Stack sx={{ width: "80%", margin: "auto" }}>
				<Typography variant="h6" sx={{ margin: "1em" }}>Companies</Typography>
				{(companies && companies.length > 0)?
					<MaterialTable
						icons={tableIcons}
						columns={[
							{
								title: "Company Name",
								field: "name",
								cellStyle: textCell,
								headerStyle: textHeader,
								render: (company) => {
									return (
										<Button
											onClick={() => {handleNameClick(company)}}
											color={(company.id === selectedCompany.id)? "secondary" : "primary"}
											sx={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
										>
											{company.name}
										</Button>
									);
								}
							},
							{
								title: "Exposure Report", field: "",
								cellStyle: dataCell,
								headerStyle: dataHeader,
								render: (company) => {
									return (company.id !== 1 && company.kynd_org_id && company.kynd_org_id !== "")?
										<Button onClick={() => {handleReportClick(company)}}><Summarize/></Button>
										:
										<Fragment/>
								}
							},
							{
								title: "KYND Status",
								field: "kynd_ready",
								cellStyle: dataCell,
								headerStyle: dataHeader,
								render: (company) => {
									return (company.kynd_ready)? <Check/> : <Pending/>;
								}
							},
							{
								title: "KYND Live",
								field: "kynd_live",
								cellStyle: dataCell,
								headerStyle: dataHeader,
								render: (company) => {
									return (company.kynd_live)?
										<Check/>
										:
										<Button onClick={() => { handleKyndLiveClick(company); }}>
											<Add sx={{ color: pink }}/>
										</Button>
								}
							},
							{
								title: "uSecure",
								field: "usecure_id",
								cellStyle: dataCell,
								headerStyle: dataHeader,
								render: (company) => {
									return (company.usecure_id)?
										<Check/>
										:
										<Button onClick={() => { handleAddUsecureClick(company); }}>
											<Add sx={{ color: pink }}/>
										</Button>
								}
							},
							{
								title: "Dark Web",
								field: "breachaware_ready",
								cellStyle: dataCell,
								headerStyle: dataHeader,
								render: (company) => {
									return (company.breachaware_id)?
										<Button onClick={() => { handleAddBreachAwareClick(company); }}>
											<Check sx={{ color: pink }}/>
										</Button>
										:
										(company.usecure_id)?
										<Button onClick={() => { handleAddBreachAwareClick(company); }}>
											<Add sx={{ color: pink }}/>
										</Button>
										:
										<React.Fragment>-</React.Fragment>
								}
							},
						]}
						actions={[
							{
								icon: () => <Edit/>,
								tooltip: "Edit",
								onClick: (event, company) => { handleEditClick(company) }
							},
							{
								icon: () => <Delete/>,
								tooltip: "Delete",
								onClick: (event, company) => { handleDeleteClick(company) }
							}
						]}
						data={companies}
						title=""
						options={defaultTableOptions}
					/>
				: <MaterialTable icons={tableIcons} title="" options={defaultTableOptions}/>}
				<Button onClick={handleAddClick}>+ Add</Button>
				<CompanyDeleteModal
					company={deleteCompany}
					onConfirm={handleConfirmDelete}
				/>
				<CompanyAddModal
					open={showAddCompany}
					onClose={handleAddClose}
					onSubmit={handleAddSubmit}
				/>
				<CompanyEditModal
					company={editCompany}
					onClose={handleEditClose}
					onSubmit={handleEditSubmit}
				/>
				<ExposureReportModal
					company={exposureReportCompany}
					onClose={handleExposureReportClose}
					onSubmit={handleExposureReportSubmit}
				/>
				<CompanyAddBreachAwareModal
					company={showAddBreachAware}
					onClose={handleAddBreachAwareClose}
					onSubmit={handleAddBreachAwareSubmit}
				/>
				<CompanyKyndLiveModal
					company={showKyndLive}
					onClose={handleKyndLiveClose}
					onSubmit={handleKyndLiveSubmit}
				/>
				<CompanyAddUsecureModal
					company={showAddUsecure}
					onClose={handleAddUsecureClose}
					onSubmit={handleAddUsecureSubmit}
				/>
				<SubmitModal
					open={showSubmitModal}
					sentSuccess={sentSuccess}
					onClose={handleSubmitModalClose}
				/>	
			</Stack>
		: <React.Fragment/> }
		</React.Fragment>
	);
}

export default AdminCompaniesTable;